<template>
  <div>
    <v-text-field
      :value="value"
      :label="label"
      :placeholder="placeholder"
      :rules="rules"
      :disabled="disabled"
      outlined
      dense
      readonly
      clearable
      prepend-inner-icon="mdi-calendar-clock"
      @click="openPicker"
      @click:clear="onClear"
    ></v-text-field>

    <v-dialog v-model="menuOpen" max-width="320" persistent>
      <v-card>
        <v-tabs v-model="activeTab" grow>
          <v-tab>Date</v-tab>
          <v-tab :disabled="!datePart">Time</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <v-date-picker
              v-model="datePart"
              no-title
              scrollable
              :first-day-of-week="1"
              full-width
              @input="onDatePicked"
            ></v-date-picker>
          </v-tab-item>
          <v-tab-item>
            <div class="pa-4">
              <div class="text-caption text--secondary mb-3">24-hour format. Hours 0–23, minutes 0–59.</div>
              <v-row dense align="center" justify="center">
                <v-col cols="5">
                  <v-text-field
                    v-model.number="hourInput"
                    type="number"
                    min="0"
                    max="23"
                    label="Hour"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="1" class="text-center">
                  <span class="text-h5">:</span>
                </v-col>
                <v-col cols="5">
                  <v-text-field
                    v-model.number="minuteInput"
                    type="number"
                    min="0"
                    max="59"
                    label="Minute"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-tab-item>
        </v-tabs-items>

        <v-card-actions>
          <v-btn text @click="onCancel">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="onConfirm" :disabled="!canConfirm"> OK </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Vue } from 'vue-property-decorator';
import moment from 'moment';

const BACKEND_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const DATE_FORMAT = 'YYYY-MM-DD';

export default Vue.extend({
  name: 'DateTimePickerField',
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: '' },
    placeholder: { type: String, default: 'YYYY-MM-DD HH:mm:ss' },
    rules: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      menuOpen: false,
      activeTab: 0,
      datePart: '',
      hourInput: 0,
      minuteInput: 0,
    };
  },
  computed: {
    canConfirm() {
      // OK button enables only when date is set and hour/minute are valid integers in range.
      if (!this.datePart) return false;
      const h = Number(this.hourInput);
      const m = Number(this.minuteInput);
      if (!Number.isInteger(h) || h < 0 || h > 23) return false;
      if (!Number.isInteger(m) || m < 0 || m > 59) return false;
      return true;
    },
  },
  watch: {
    menuOpen(open) {
      // When the menu opens, parse the current value into date and time parts.
      if (open) {
        this.activeTab = 0;
        if (this.value) {
          const m = moment(this.value, BACKEND_FORMAT, true);
          if (m.isValid()) {
            this.datePart = m.format(DATE_FORMAT);
            this.hourInput = m.hour();
            this.minuteInput = m.minute();
          } else {
            // Try parsing in any reasonable format (e.g. ISO leaking through).
            const fallback = moment(this.value);
            if (fallback.isValid()) {
              this.datePart = fallback.format(DATE_FORMAT);
              this.hourInput = fallback.hour();
              this.minuteInput = fallback.minute();
            } else {
              this.datePart = '';
              this.hourInput = 0;
              this.minuteInput = 0;
            }
          }
        } else {
          this.datePart = '';
          this.hourInput = 0;
          this.minuteInput = 0;
        }
      }
    },
  },
  methods: {
    openPicker() {
      // Don't open when the field is disabled.
      if (this.disabled) return;
      this.menuOpen = true;
    },
    onDatePicked() {
      // Auto-advance to the Time tab once a date is picked.
      this.activeTab = 1;
    },
    onConfirm() {
      // Combine date + hour + minute + ":00" seconds into the backend format and emit.
      // Seconds are always "00" — minute-granularity is sufficient for contracts.
      if (!this.canConfirm) return;
      const hh = String(this.hourInput).padStart(2, '0');
      const mm = String(this.minuteInput).padStart(2, '0');
      const combined = moment(`${this.datePart} ${hh}:${mm}:00`, BACKEND_FORMAT, true).format(BACKEND_FORMAT);
      this.$emit('input', combined);
      this.menuOpen = false;
    },
    onCancel() {
      this.menuOpen = false;
    },
    onClear() {
      this.$emit('input', '');
      this.datePart = '';
      this.hourInput = 0;
      this.minuteInput = 0;
      this.menuOpen = false;
    },
  },
});
</script>
